import { BaseRequest } from "./BaseRequest";
import { Chat } from "./models/Chat";
import { ChatMessage } from "./models/ChatMessage";
import { ChatUser } from "./models/ChatUser";
import { ChatAccess } from "./models/ChatAccess";
import { Organization } from "./models/Organization";
import { User } from "./models/User";
import { ICommandResult } from "./models/CommandResult";
import { UserInfo } from "./models/UserInfo";

export class ChatApiService extends BaseRequest {
    /**
     * Получение списка пользователей чата
     * @param chatId
     * @param config
     * @returns
     */
    getUsers(
        chatId: string | null,
        config?: Object
    ): Promise<ICommandResult<ChatUser[]>> {
        return this.fetchAPI(
            `/api/chat/users?chatId=${chatId}`,
            Object.assign({method: "GET"}, config)
        )
            .catch(BaseRequest.handleError);
    }
    /**
     * Получение фото пользователя
     * @param sessionId
     * @param userId
     * @param config
     * @returns
     */
    getUserPhoto(
        sessionId: string | null,
        userId: string | null,
        config?: Object
    ): Promise<ICommandResult<string>> {
        return this.fetchAPI(
            `/api/Chat/user/photo/${userId}?sessionId=${sessionId}`,
            Object.assign({method: "GET"}, config)
        )
            .catch(BaseRequest.handleError);
    }

    /**
     * Получение списка сообщений чата
     * @param chatId
     * @param config
     * @returns
     */
    getMessages(
        chatId: string | null,
        config?: Object
    ): Promise<ICommandResult<ChatMessage[]>> {
        return this.fetchAPI(
            `/api/chat/messages?chatId=${chatId}`,
            Object.assign({method: "GET"}, config)
        )
            .catch(BaseRequest.handleError);
    }

    /**
     * Получение списка чатов пользователя
     * @param userId
     * @param config
     * @returns
     */
    getChats(
        userId: string | null,
        sessionId: string | null,
        roomType?: string | null,
        config?: Object
    ): Promise<ICommandResult<Chat[]>> {
        let req = "";
        if (userId !== null) {
            req += `userId=${userId}`;
        }
        if (sessionId !== null) {
            req += `${req.length > 0 ? '&' : ''}sessionId=${sessionId}`
        }
        if (roomType) {
            req += `${req.length > 0 ? '&' : ''}roomType=${roomType}`;
        }
        return this.fetchAPI(
            `/api/Chat/list?${req}`,
            Object.assign({method: "GET"}, config)
        )
            .catch(BaseRequest.handleError);
    }

    /**
     * Получение списка чатов пользователя
     * @param userId
     * @param config
     * @returns
     */
    getChat(
        chatId: string,
        userId: string | null,
        sessionId: string | null,
        config?: Object
    ): Promise<ICommandResult<Chat>> {
        let req = "";
        if (userId !== null) {
            req += `userId=${userId}`;
        }
        if (sessionId !== null) {
            req += `${req.length > 0 ? '&' : ''}sessionId=${sessionId}`
        }
        return this.fetchAPI(
            `/api/Chat/${chatId}?${req}`,
            Object.assign({method: "GET"}, config)
        )
            .catch(BaseRequest.handleError);
    }
    
    /**
     * Получение списка сообщений чата
     * @param chatId
     * @param userId
     * @param config
     * @returns
     */
    getChatUnreadMessages(
        chatId: string | null,
        userId: string | null,
        config?: Object
    ): Promise<ICommandResult<ChatMessage[]>> {
        return this.fetchAPI(
            `/api/Chat/chat-unread-messages?chatId=${chatId}&userId=${userId}`,
            Object.assign({method: "GET"}, config)
        )
            .catch(BaseRequest.handleError);
    }

    /**
     * Получение доступы к чату пользователем
     * @param chatId
     * @param userId
     * @param config
     * @returns
     */
    getChatUserAccess(
        chatId: string | null,
        userId: string | null,
        config?: Object
    ): Promise<ICommandResult<ChatAccess>> {
        return this.fetchAPI(
            `/api/Chat/chat-user-access?chatId=${chatId}&userId=${userId}`,
            Object.assign({method: "GET"}, config)
        )
            .catch(BaseRequest.handleError);
    }

    /**
     * Изменить доступ к чату
     * @param userId
     * @param chatId
     * @param config
     * @returns
     */
    changeReadAccess(
        userId: string | null,
        chatId: string | null,
        config?: Object
    ): Promise<ICommandResult<null>> {
        return this.fetchAPI(
            `/api/chat/change-read-access`,
            Object.assign({method: "POST", body: JSON.stringify({userId, chatId, access: false})}, config)
        )
            .catch(BaseRequest.handleError);
    }

    /**
     * Изменить видимость чата
     * @param userId
     * @param chatId
     * @param access
     * @param config
     * @returns
     */
    changeVisible(
        userId: string | null,
        chatId: string | null,
        access = false,
        config?: Object
    ): Promise<ICommandResult<null>> {
        return this.fetchAPI(
            `/api/chat/change-visible`,
            Object.assign({method: "POST", body: JSON.stringify({userId, chatId, access})}, config)
        )
            .catch(BaseRequest.handleError);
    }

    /**
     * Изменить админский доступ к чату
     * @param userId
     * @param chatId
     * @param config
     * @returns
     */
    changeIsAdminAccess(
        userId: string | null,
        chatId: string | null,
        isAdmin: boolean,
        config?: Object
    ): Promise<ICommandResult<null>> {
        return this.fetchAPI(
            `/api/Chat/change-is-admin`,
            Object.assign({method: "POST", body: JSON.stringify({userId, chatId, isAdmin})}, config)
        )
            .catch(BaseRequest.handleError);
    }

    /**
     * Получение списк организаций
     * @param config
     * @returns
     */
    getOrganizations(
        sessionId: string,
        config?: Object
    ): Promise<ICommandResult<Organization[]>> {
        return this.fetchAPI(
            `/api/Chat/organizations?sessionId=${sessionId}`,
            Object.assign({method: "GET"}, config)
        )
            .catch(BaseRequest.handleError);
    }

    /**
     * Получить информацию о пользователе
     * @param config
     * @returns
     */
    getUserInfo(
        sessionId: string,
        config?: Object
    ): Promise<ICommandResult<UserInfo>> {
        return this.fetchAPI(
            `/api/Chat/user-info?sessionId=${sessionId}`,
            Object.assign({method: "GET"}, config)
        )
            .catch(BaseRequest.handleError);
    }

    /**
     * Поиск контактов
     * @param search
     * @param organizationId
     * @param config
     * @returns
     */
    searchUsers(
        sessionId: string,
        search: string | null,
        organizationId: string | null,
        config?: Object
    ): Promise<ICommandResult<User[]>> {
        return this.fetchAPI(
            `/api/Chat/contacts?search=${search}&sessionId=${sessionId}${(organizationId ? `&organizationId=${organizationId}` : '')}`,
            Object.assign({method: "GET"}, config)
        )
            .catch(BaseRequest.handleError);
    }
}